import React from 'react';
import { TextField } from '@/shared/components/TextField';
import { SUBSCRIPTION_MANAGEMENT_LABELS } from '@/epics/subscriptionManagment/constants/translations/labels';
import { EQUIPMENT_RANK_COUNT } from '@/epics/subscriptionManagment/hooks/useCreateSubscription';

export interface DiscountTierRowProps {
  rank: number;
  discountPercentage: string;
  onDiscountPercentageChange: (value: string) => void;
  error?: string | undefined;
  disabled?: boolean;
}

/** Digits + one decimal point, capped at 2 decimal places, clamped 0-100. */
function sanitizeDiscountInput(rawValue: string): string {
  const filtered = rawValue.replace(/[^\d.]/g, '');
  const [rawIntPart = '', decPart] = filtered.split('.');
  const intPart = rawIntPart.replace(/^0+(?=\d)/, '');
  const cleaned =
    decPart !== undefined ? `${intPart}.${decPart.slice(0, 2)}` : intPart;
  if (cleaned === '' || cleaned === '.') {
    return '';
  }
  const num = Number(cleaned);
  if (Number.isNaN(num)) {
    return '';
  }
  return num > 100 ? '100' : cleaned;
}

/** One row of the Discount Logic step: equipment rank (1-7) and its discount %. */
export const DiscountTierRow: React.FC<DiscountTierRowProps> = ({
  rank,
  discountPercentage,
  onDiscountPercentageChange,
  error,
  disabled = false,
}) => (
  <div
    data-field={`discount-tier-${rank}`}
    className="grid grid-cols-2 items-center"
  >
    <span
      style={{
        color: 'var(--Schemes-On-Surface-Variant, #474A4C)',
        fontSize: 18,
        fontFamily: 'Roboto',
        fontWeight: '400',
        lineHeight: '26px',
        wordWrap: 'break-word',
      }}
    >
      {SUBSCRIPTION_MANAGEMENT_LABELS.EQUIPMENT_RANK_PREFIX.polish} {rank}
      {rank === EQUIPMENT_RANK_COUNT ? '+' : ''}
    </span>
    <div>
      <TextField
        label={
          SUBSCRIPTION_MANAGEMENT_LABELS.FIELD_PLAN_DISCOUNT_PERCENTAGE.polish
        }
        type="text"
        inputMode="decimal"
        width={320}
        value={discountPercentage}
        onChange={(e) => {
          onDiscountPercentageChange(sanitizeDiscountInput(e.target.value));
        }}
        error={!!error}
        errorMessage={error ?? ''}
        disabled={disabled}
        trailingIcon={
          discountPercentage ? (
            <span className="text-bodyLarge text-neutral-10 align-middle ml-[2px] mt-[12px]">
              %
            </span>
          ) : null
        }
      />
    </div>
  </div>
);

export default DiscountTierRow;
